import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, MenuItem, Chip,
  InputAdornment, Collapse
} from "@mui/material";
import SearchIcon        from "@mui/icons-material/Search";
import FilterListIcon    from "@mui/icons-material/FilterList";
import LocationOnIcon    from "@mui/icons-material/LocationOn";
import PersonIcon        from "@mui/icons-material/Person";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon    from "@mui/icons-material/ExpandMore";
import ExpandLessIcon    from "@mui/icons-material/ExpandLess";

import { soldBikeService } from "../service/SoldBikeService";
import BikeDetailModal     from "../component/Bikedetailmodal"; // ← same modal as SoldBikeAdminView

// ─── THEME ────────────────────────────────────────────────────────────────────
const LOCATIONS = ["Boroda", "Chaitanyapur", "Mahisadal"];
const LOC_COLOR = {
  Boroda:       { bg: "#e8f4fd", border: "#1976d2", badge: "#1565c0", light: "#bbdefb" },
  Chaitanyapur: { bg: "#f3e5f5", border: "#7b1fa2", badge: "#6a1b9a", light: "#e1bee7" },
  Mahisadal:    { bg: "#e8f5e9", border: "#2e7d32", badge: "#1b5e20", light: "#c8e6c9" },
  Other:        { bg: "#fff8e1", border: "#f57f17", badge: "#e65100", light: "#ffecb3" }
};

// ─── SALE TYPE CHIP ───────────────────────────────────────────────────────────
const SaleTypeChip = ({ saleType }) => {
  const cfg = {
    CASH:      { bg: "#e8f5e9", color: "#2e7d32" },
    FINANCE:   { bg: "#e3f2fd", color: "#1565c0" },
    EXCHANGE:  { bg: "#fff3e0", color: "#e65100" },
    "EX+FIN":  { bg: "#ede7f6", color: "#4527a0" },
    "CASH+EX": { bg: "#fce4ec", color: "#ad1457" },
  };
  const style = cfg[saleType] || { bg: "#f5f5f5", color: "#555" };
  return (
    <Chip
      label={saleType || "—"}
      size="small"
      sx={{ fontSize: "0.65rem", fontWeight: 700, bgcolor: style.bg, color: style.color }}
    />
  );
};

// ─── PAYMENT INFO CELL ───────────────────────────────────────────────────────
const PaymentInfoCell = ({ bike }) => {
  const saleType = bike?.saleType || "";
  const dash = <Typography variant="caption" color="text.secondary">—</Typography>;
  const rows = [];

  if (saleType === "CASH" || saleType === "CASH+EX" ||
      ((saleType === "FINANCE" || saleType === "EX+FIN") && bike?.cashAmount)) {
    rows.push(
      <Box key="cash" display="flex" alignItems="center" gap={0.5}>
        <Chip label="CASH" size="small" sx={{ fontSize: "0.6rem", fontWeight: 700, bgcolor: "#e8f5e9", color: "#2e7d32", height: 18 }} />
        <Typography variant="caption" fontWeight={700} color="#2e7d32">
          {bike?.cashAmount != null ? `₹${bike.cashAmount.toLocaleString("en-IN")}` : dash}
        </Typography>
      </Box>
    );
  }

  if (saleType === "FINANCE" || saleType === "EX+FIN") {
    rows.push(
      <Box key="finance" display="flex" flexDirection="column" gap={0.2}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip label="FIN" size="small" sx={{ fontSize: "0.6rem", fontWeight: 700, bgcolor: "#e3f2fd", color: "#1565c0", height: 18 }} />
          <Typography variant="caption" fontWeight={700} color="#1565c0">
            {bike?.finance?.companyName || dash}
          </Typography>
        </Box>
        <Typography variant="caption" color="#1565c0" sx={{ pl: 0.5 }}>
          {bike?.finance?.amount != null ? `₹${bike.finance.amount.toLocaleString("en-IN")}` : dash}
        </Typography>
      </Box>
    );
  }

  if (saleType === "EXCHANGE" || saleType === "EX+FIN" || saleType === "CASH+EX") {
    rows.push(
      <Box key="exchange" display="flex" alignItems="center" gap={0.5}>
        <Chip label="EX" size="small" sx={{ fontSize: "0.6rem", fontWeight: 700, bgcolor: "#fff3e0", color: "#e65100", height: 18 }} />
        <Typography variant="caption" fontWeight={700} color="#e65100">
          {bike?.exchange?.exchangePrice != null
            ? `₹${bike.exchange.exchangePrice.toLocaleString("en-IN")}`
            : dash}
        </Typography>
      </Box>
    );
  }

  if (rows.length === 0) return dash;
  return <Box display="flex" flexDirection="column" gap={0.4}>{rows}</Box>;
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const CustomerDetails = () => {

  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [expandedLoc,  setExpandedLoc]  = useState({});
  const [filtersOpen,  setFiltersOpen]  = useState(true);

  // ── Same modal state as SoldBikeAdminView ─────────────────────────────────
  const [open,          setOpen]          = useState(false);
  const [selectedBike,  setSelectedBike]  = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [filters, setFilters] = useState({
    model: "", location: "", customerName: "", mobile: "",
    saleType: "", paymentStatus: "", engineNumber: "", chasisNumber: "",
    fromDate: "", toDate: "", month: "", year: "", search: ""
  });

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const res   = await soldBikeService.getFilteredSoldBikes(filters);
      const bikes = res?.data || [];

      const grouped = Object.values(
        bikes.reduce((acc, bike) => {
          const loc = bike.location || "Other";
          if (!acc[loc]) acc[loc] = { location: loc, bikes: [] };
          acc[loc].bikes.push(bike);
          return acc;
        }, {})
      );

      setData(grouped);
      const exp = {};
      grouped.forEach((g) => { exp[g.location] = true; });
      setExpandedLoc(exp);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleChange = (e) =>
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  const resetFilters = () => {
    setFilters({
      model: "", location: "", customerName: "", mobile: "",
      saleType: "", paymentStatus: "", engineNumber: "", chasisNumber: "",
      fromDate: "", toDate: "", month: "", year: "", search: ""
    });
    setTimeout(fetchData, 0);
  };

  const toggleLoc = (loc) =>
    setExpandedLoc((p) => ({ ...p, [loc]: !p[loc] }));

  // ── View — identical to SoldBikeAdminView ─────────────────────────────────
  const handleView = async (id) => {
    try {
      setOpen(true);
      setDetailLoading(true);
      const res = await soldBikeService.getSpecificBike(id);
      setSelectedBike(res?.data || null);
    } catch {
      setError("Failed to load details");
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Grand totals ───────────────────────────────────────────────────────────
  const allBikes   = data.flatMap((g) => g.bikes || []);
  const totalCount = allBikes.length;
  const totalRev   = allBikes.reduce((s, b) => s + (b.salePrice || 0), 0);
  const dueCount   = allBikes.filter((b) => b?.due?.status === "DUE").length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2 }}>

      {/* ── HEADER ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ bgcolor: "#1565c0", borderRadius: 2, p: 1, display: "flex" }}>
            <PersonIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0d1b2a" letterSpacing={-0.5}>
              Customer Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalCount} sold · {dueCount} due · ₹{totalRev.toLocaleString("en-IN")} total revenue
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<FilterListIcon />}
          variant={filtersOpen ? "contained" : "outlined"}
          size="small"
          onClick={() => setFiltersOpen((p) => !p)}
          sx={{ borderRadius: 2, fontWeight: 700, fontSize: "0.75rem" }}
        >
          {filtersOpen ? "Hide Filters" : "Show Filters"}
        </Button>
      </Box>

      {/* ── FILTERS ── */}
      <Collapse in={filtersOpen}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: "1px solid #e0e7ef", borderRadius: 3 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={2}>
              <TextField label="Model" name="model" fullWidth size="small"
                value={filters.model} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Location" name="location" fullWidth size="small"
                value={filters.location} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {LOCATIONS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Customer" name="customerName" fullWidth size="small"
                value={filters.customerName} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Mobile" name="mobile" fullWidth size="small"
                value={filters.mobile} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Sale Type" name="saleType" fullWidth size="small"
                value={filters.saleType} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="CASH">CASH</MenuItem>
                <MenuItem value="FINANCE">FINANCE</MenuItem>
                <MenuItem value="EXCHANGE">EXCHANGE</MenuItem>
                <MenuItem value="EX+FIN">EX+FIN</MenuItem>
                <MenuItem value="CASH+EX">CASH+EX</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Due Status" name="paymentStatus" fullWidth size="small"
                value={filters.paymentStatus} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PAID">PAID</MenuItem>
                <MenuItem value="DUE">DUE</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Engine No" name="engineNumber" fullWidth size="small"
                value={filters.engineNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Serial No" name="chasisNumber" fullWidth size="small"
                value={filters.chasisNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="From Date" name="fromDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.fromDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField type="date" label="To Date" name="toDate" fullWidth size="small"
                InputLabelProps={{ shrink: true }} value={filters.toDate} onChange={handleChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select label="Month" name="month" fullWidth size="small"
                value={filters.month} onChange={handleChange}>
                <MenuItem value="">All</MenuItem>
                {[...Array(12)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Year" name="year" type="number" fullWidth size="small"
                value={filters.year} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Search" name="search" fullWidth size="small"
                value={filters.search} onChange={handleChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Button fullWidth variant="contained" onClick={fetchData}
                sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#1565c0" }}>
                Apply Filter
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button fullWidth variant="outlined" onClick={resetFilters}
                sx={{ borderRadius: 2, fontWeight: 700 }}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* ── STATES ── */}
      {loading && <Box textAlign="center" py={6}><CircularProgress /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && data.length === 0 && (
        <Alert severity="info">No records found for selected filters.</Alert>
      )}

      {/* ── LOCATION WISE TABLE ── */}
      {!loading && data.map((group) => {
        const location = group.location || "Other";
        const theme    = LOC_COLOR[location] || LOC_COLOR.Other;
        const isOpen   = expandedLoc[location] !== false;
        const bikes    = group.bikes || [];
        const dueC     = bikes.filter((b) => b?.due?.status === "DUE").length;
        const paidC    = bikes.filter((b) => b?.due?.status === "PAID").length;
        const rev      = bikes.reduce((s, b) => s + (b.salePrice || 0), 0);

        return (
          <Box key={location} mb={3}>

            {/* Location Header */}
            <Paper elevation={0} onClick={() => toggleLoc(location)}
              sx={{
                px: 2.5, py: 1.5,
                background: `linear-gradient(135deg, ${theme.bg} 0%, #fff 100%)`,
                border: `2px solid ${theme.border}`,
                borderBottom: isOpen ? "none" : `2px solid ${theme.border}`,
                borderRadius: isOpen ? "12px 12px 0 0" : "12px",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <LocationOnIcon sx={{ color: theme.badge, fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={800} color={theme.badge}>
                  {location}
                </Typography>
                <Chip label={`${bikes.length} customers`} size="small"
                  sx={{ bgcolor: theme.badge, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
              </Box>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                {dueC > 0 && (
                  <Chip icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
                    label={`${dueC} DUE`} size="small"
                    sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700, border: "1px solid #ffb74d", fontSize: "0.68rem" }} />
                )}
                <Chip icon={<CheckCircleIcon sx={{ fontSize: 13 }} />}
                  label={`${paidC} PAID`} size="small"
                  sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, border: "1px solid #a5d6a7", fontSize: "0.68rem" }} />
                <Typography variant="body2" fontWeight={700} color={theme.badge}>
                  ₹{rev.toLocaleString("en-IN")}
                </Typography>
                {isOpen
                  ? <ExpandLessIcon sx={{ color: theme.badge }} />
                  : <ExpandMoreIcon sx={{ color: theme.badge }} />}
              </Box>
            </Paper>

            {/* Table */}
            <Collapse in={isOpen}>
              <Paper elevation={0} sx={{
                border: `2px solid ${theme.border}`, borderTop: "none",
                borderRadius: "0 0 12px 12px", overflow: "hidden"
              }}>
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: theme.light }}>
                        {["#", "Invoice", "Model", "Customer", "Mobile", "Sale Price", "Sale Type", "Payment Info", "Due Status", "Sale Date", "Action"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 800, fontSize: "0.7rem", color: theme.badge, letterSpacing: 0.5, whiteSpace: "nowrap", py: 1.2 }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bikes.map((b, idx) => {
                        const isDue = b?.due?.status === "DUE";
                        return (
                          <TableRow key={b._id}
                            sx={{
                              bgcolor: isDue ? "#fff8f5" : idx % 2 === 0 ? "#fff" : "#fafbff",
                              "&:hover": { bgcolor: isDue ? "#fff0eb" : "#f0f4ff" },
                              transition: "background 0.15s"
                            }}
                          >
                            <TableCell sx={{ color: "#aaa", fontSize: "0.73rem", fontWeight: 600 }}>{idx + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#1565c0", whiteSpace: "nowrap" }}>
                              {b.invoiceNo || "—"}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                              {b.model}
                              {b.color && <Typography variant="caption" display="block" color="text.secondary">{b.color}</Typography>}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700}>{b.customerName}</Typography>
                              {b.fatherName && <Typography variant="caption" color="text.secondary">S/O {b.fatherName}</Typography>}
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                    {Array.isArray(b.mobile) ? b.mobile.join(", ") : b.mobile || "—"}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                              ₹{(b.salePrice || 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell>
                              <SaleTypeChip saleType={b.saleType} />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <PaymentInfoCell bike={b} />
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={isDue
                                  ? <WarningAmberIcon sx={{ fontSize: 12 }} />
                                  : <CheckCircleIcon  sx={{ fontSize: 12 }} />}
                                label={b?.due?.status || "PAID"} size="small"
                                sx={{
                                  fontSize: "0.65rem", fontWeight: 700,
                                  bgcolor: isDue ? "#fff3e0" : "#e8f5e9",
                                  color:   isDue ? "#e65100" : "#2e7d32",
                                  border:  `1px solid ${isDue ? "#ffb74d" : "#a5d6a7"}`
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.73rem", color: "#666", whiteSpace: "nowrap" }}>
                              {b.saleDate ? new Date(b.saleDate).toLocaleDateString("en-IN") : "—"}
                            </TableCell>
                            <TableCell>
                              {/* ── Same View button as SoldBikeAdminView — passes b._id ── */}
                              <Button size="small" variant="outlined"
                                onClick={() => handleView(b._id)}
                                sx={{ fontSize: "0.7rem", fontWeight: 700, borderRadius: 1.5, px: 1.2, py: 0.4, minWidth: 0 }}>
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Paper>
            </Collapse>
          </Box>
        );
      })}

      {/* ── BikeDetailModal — same as SoldBikeAdminView ── */}
      <BikeDetailModal
        open={open}
        onClose={() => setOpen(false)}
        selectedBike={selectedBike}
        detailLoading={detailLoading}
      />

    </Box>
  );
};

export default CustomerDetails;